import plur from 'plur';
import { formatOneOfValue } from '@/features/condition-viewer';
import { OPERATOR_DEFINITIONS, isOneOfOperator, operatorArity } from '@/features/dryad';
import { isRecord } from '@/utils/objects';

/**
 * How a segment's Usage column words one condition that reads an attribute.
 *
 * The column is Ember's older condition viewer
 * (`cio-condition-viewer-attribute`, reached through `segmentViewComponent`
 * from `data-index-item/segments/row/condition-list`), not the viewer the
 * segment's hover card draws. The two word the same operator differently —
 * "is equal to" here, "is" in the card — so this reads the operator table that
 * viewer reads (`cio/utils/operators.ts`, ported as dryad's vocabulary)
 * rather than the shared viewer's.
 */

/** The attribute a condition reads, and what it asks of it. */
export type AttributeConditionReading = {
  name: string;
  /** The operator, negated with a `!` prefix the way Ember's model spells it. */
  operator: string;
  value: string | null;
  value2: string | null;
};

/**
 * Each operator's label, declared negations included, from the vocabulary
 * Hydra already ports from Ember's `cio/utils/operators.ts`.
 */
const LABELS = new Map<string, string>(
  OPERATOR_DEFINITIONS.flatMap((definition) => {
    const own: [string, string] = [definition.value, definition.label];
    return 'negation' in definition ? [own, [definition.negation.value, definition.negation.label]] : [own];
  }),
);

function withoutNegation(operator: string): string {
  return operator.startsWith('!') ? operator.slice(1) : operator;
}

/**
 * The words an operator reads as.
 *
 * A `!` the vocabulary does not declare — the wire can negate any operator —
 * reads as its base with the negated wording (`labelOfOperator`), and anything
 * outside the vocabulary as itself.
 */
export function operatorLabel(operator: string): string {
  const declared = LABELS.get(operator);
  if (declared != null) {
    return declared;
  }
  const base = operator.startsWith('!') ? LABELS.get(withoutNegation(operator)) : undefined;
  if (base == null) {
    return operator;
  }
  return base.startsWith('is ') ? `is not ${base.slice('is '.length)}` : `not ${base}`;
}

/**
 * What the operator asks of a value: none, one, two, or a number of days from
 * now. An operator outside the vocabulary takes one, as Ember's
 * `isBinaryOperator` has it.
 */
export type OperatorShape = 'none' | 'one' | 'two' | 'relative-days';

export function operatorShape(operator: string): OperatorShape {
  const base = withoutNegation(operator);
  if (base === 'timestamp_gt' || base === 'timestamp_lt') {
    return 'relative-days';
  }
  const arity = operatorArity(operator);
  if (arity === 0) {
    return 'none';
  }
  return arity === 2 ? 'two' : 'one';
}

/** Whether the operator's values are epoch seconds, drawn as dates. */
function isAbsoluteTimestamp(operator: string): boolean {
  const base = withoutNegation(operator);
  return base === 'timestamp_gta' || base === 'timestamp_lta' || base === 'timestamp_bw';
}

function readString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value;
  }
  return typeof value === 'number' || typeof value === 'boolean' ? String(value) : null;
}

/**
 * The attribute condition a stored condition is, or `null` for any other kind.
 *
 * The detail read sends each condition as builder JSON: an `attribute_change`
 * event whose first filter carries the operator and whose `inverse` negates
 * it (`deserialize/attribute.ts`).
 */
export function readAttributeCondition(condition: unknown): AttributeConditionReading | null {
  if (!isRecord(condition) || !isRecord(condition.event)) {
    return null;
  }
  const { event } = condition;
  // Ember reads `ui_type || type`: an empty `ui_type` defers to `type`.
  const type = typeof event.ui_type === 'string' && event.ui_type !== '' ? event.ui_type : event.type;
  if (type !== 'attribute_change') {
    return null;
  }
  const name = readString(event.name);
  const filters = isRecord(event.filters) ? event.filters.and : undefined;
  const filter: unknown = Array.isArray(filters) ? filters[0] : undefined;
  const operator = isRecord(filter) ? readString(filter.operator) : null;
  if (name == null || operator == null || !isRecord(filter)) {
    return null;
  }
  return {
    name,
    operator: filter.inverse === true ? `!${operator}` : operator,
    value: readString(filter.value),
    value2: readString(filter.value2),
  };
}

/** Seconds in a day, which a relative timestamp is counted in. */
const SECONDS_PER_DAY = 86_400;

/** A relative timestamp's value as Ember draws it: a whole number of days, and which way. */
export type RelativeDays = { days: number; unit: string; direction: 'ago' | 'from now' };

export function relativeDays(value: string | null): RelativeDays {
  const seconds = Number.parseInt(value ?? '', 10);
  const days = Math.floor((Number.isNaN(seconds) ? 0 : seconds) / SECONDS_PER_DAY);
  const count = Math.abs(days);
  return { days: count, unit: plur('day', count), direction: days < 0 ? 'ago' : 'from now' };
}

/** What `moment-format` writes for a value that is not a date. */
const INVALID_DATE = 'Invalid date';

const ABSOLUTE_TIMESTAMP_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});

/**
 * An epoch in seconds as Ember's `moment-format` draws it in GMT, with the
 * app's output format `MMM D, YYYY [at] h:mm:ss a [(]z[)]`.
 */
function formatAbsoluteTimestamp(value: string): string {
  const instant = new Date(Number(value) * 1000);
  // A value that is no date — a redacted `******`, or one past the range a
  // date can hold — reads as moment writes it, rather than throwing.
  if (Number.isNaN(instant.getTime())) {
    return INVALID_DATE;
  }
  const parts = new Map(ABSOLUTE_TIMESTAMP_FORMAT.formatToParts(instant).map((part) => [part.type, part.value]));
  const date = `${parts.get('month') ?? ''} ${parts.get('day') ?? ''}, ${parts.get('year') ?? ''}`;
  const pad = (part: 'minute' | 'second') => (parts.get(part) ?? '').padStart(2, '0');
  const time = `${parts.get('hour') ?? ''}:${pad('minute')}:${pad('second')}`;
  return `${date} at ${time} ${(parts.get('dayPeriod') ?? '').toLowerCase()} (GMT)`;
}

/**
 * One value as the condition draws it, or `null` for an empty one — which
 * Ember draws as `""` rather than as an empty chip.
 */
export function displayValue(operator: string, value: string | null): string | null {
  const shown = isOneOfOperator(operator) ? formatOneOfValue(value) : value;
  // Strictly the empty string, not a blank one: a blank absolute timestamp
  // is `Number('  ')`, the epoch, which Ember's `moment.unix` draws too.
  // A blank value of any other kind is the caller's to word.
  if (shown == null || shown === '') {
    return null;
  }
  return isAbsoluteTimestamp(operator) ? formatAbsoluteTimestamp(shown) : shown;
}
